import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate, useParams } from "react-router-dom";
import TablePagination from "./TablePagination";
import { getManagerClientStatusUpdates, getManagerById } from "../Apis/ManagerAPIs";
import { formatDateTime, formatDate } from "../helper/DateHelper";
import { toast } from "react-toastify";
import StandardDatePicker from "./StandardDatePicker";

const EventManagerHistoryLayer = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterDate, setFilterDate] = useState(() => {
    const d = new Date();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  });
  const [statusFilter, setStatusFilter] = useState("All");
  const [reportData, setReportData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [managerName, setManagerName] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const handleClearFilters = () => {
    setSearchTerm("");
    const d = new Date();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    setFilterDate(`${year}-${month}-${day}`);
    setStatusFilter("All");
    setCurrentPage(0);
  };

  const fetchManagerInfo = async () => {
    try {
      const result = await getManagerById(id);
      if (result.status && result.response) {
        const data = result.response.data || result.response;
        setManagerName(data.name || "");
      }
    } catch (error) {
      console.error("Error fetching manager details:", error);
    }
  };

  const fetchHistoryData = async (page = currentPage, limit = rowsPerPage) => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm || undefined,
        date: filterDate || undefined,
        status: statusFilter !== "All" ? statusFilter : undefined,
        page: page,
        limit: limit,
      };
      const result = await getManagerClientStatusUpdates(id, params);
      if (result.status && result.response) {
        setReportData(result.response.data || []);
        setSummaryData(result.response.summary || []);
        setTotalRecords(result.response.total || (result.response.data ? result.response.data.length : 0));
        setTotalPages(result.response.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching client status updates:", error);
      toast.error("Failed to fetch status update history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchManagerInfo();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchHistoryData(currentPage, rowsPerPage);
    }
  }, [id, searchTerm, filterDate, statusFilter, currentPage, rowsPerPage]);

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "booked":
      case "won":
        return "bg-success-50 text-success-600 border border-success-200";
      case "in discussions":
      case "discussion":
      case "in discussion":
        return "bg-info-50 text-info-600 border border-info-200";
      case "potential":
      case "prospect":
        return "bg-warning-50 text-warning-600 border border-warning-200";
      case "not interested":
      case "lost":
        return "bg-danger-50 text-danger-600 border border-danger-200";
      default:
        return "bg-neutral-50 text-neutral-600 border border-neutral-200";
    }
  };

  return (
    <div className="card h-100 p-0 radius-12 overflow-hidden border-0 shadow-sm">
      <div
        className="card-header border-bottom py-12 px-24 d-flex align-items-center justify-content-between"
        style={{ background: "linear-gradient(135deg, #000000, #bb0007, #ed1c24)" }}
      >
        <h6 className="text-white mb-0 fw-bold">
          MANAGER CLIENT STATUS HISTORY {managerName ? `(${managerName})` : ""}
        </h6>

        <div className="d-flex align-items-center gap-3">
          <div className="position-relative" style={{ width: "280px" }}>
            <input
              type="text"
              className="form-control form-control-sm radius-8 ps-32"
              placeholder="Search Client or Status..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(0);
              }}
              style={{
                backgroundColor: "#fff",
                border: "1px solid rgba(255,255,255,0.2)",
                color: "black",
                fontWeight: "500",
              }}
            />
            <Icon
              icon="solar:magnifer-linear"
              className="position-absolute start-0 top-50 translate-middle-y ms-12 text-dark opacity-75"
            />
          </div>
          <button
            onClick={() => navigate(-1)}
            className="btn btn-sm radius-8 px-16 py-8 fw-bold d-flex align-items-center gap-2 text-dark shadow-sm transition-all hover-scale-105"
            style={{ fontSize: "12px", backgroundColor: "#f7e7da" }}
          >
            <Icon icon="solar:arrow-left-bold" />
            Back to List
          </button>
        </div>
      </div>

      <div className="card-body p-16">

        <div className="p-16 radius-8 mb-16" style={{ backgroundColor: "#f7e7da" }}>
          <div className="row g-3 align-items-end">
            <div className="col-md-3 col-sm-6">
              <label className="form-label fw-semibold text-secondary-light mb-4" style={{ fontSize: "13px" }}>
                Date:
              </label>
              <StandardDatePicker
                name="filterDate"
                value={filterDate}
                onChange={(e) => {
                  setFilterDate(e.target.value);
                  setCurrentPage(0);
                }}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <label className="form-label fw-semibold text-secondary-light mb-4" style={{ fontSize: "13px" }}>
                Status:
              </label>
              <select
                className="form-select radius-8"
                style={{ height: "38px", fontSize: "13px" }}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(0);
                }}
              >
                <option value="All">All Status</option>
                <option value="Assign">Assignes</option>
                <option value="Prospect">Prospects</option>
                <option value="Followups">Followups</option>
                <option value="Not interest">Not Interested</option>
                <option value="RNR">RNR</option>
                <option value="Block">Block</option>
                <option value="Booked">Booked</option>
              </select>
            </div>
            <div className="col-md-3 col-sm-6 d-flex align-items-end gap-2">
              <button
                type="button"
                onClick={handleClearFilters}
                className="btn btn-sm radius-8 px-16 py-8 fw-bold d-flex align-items-center gap-2 text-white shadow-sm transition-all hover-scale-105"
                style={{ height: "38px", fontSize: "12px", backgroundColor: "#ed1c24", border: "none" }}
              >
                <Icon icon="solar:restart-bold" className="text-lg" />
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {summaryData && summaryData.length > 0 && (
          <div className="table-responsive border radius-8 bg-white overflow-x-auto shadow-sm mb-24">
            <h6 className="fw-bold px-16 py-12 mb-0 border-bottom" style={{ backgroundColor: "#f8f9fa", fontSize: "14px", color: "#333" }}>Manager Status Summary</h6>
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr style={{ backgroundColor: "#f1f5f9" }}>
                  <th scope="col" className="fw-bold py-12 text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>Expo Name</th>
                  <th scope="col" className="fw-bold py-12 text-center text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>Assigned</th>
                  <th scope="col" className="fw-bold py-12 text-center text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>RNR</th>
                  <th scope="col" className="fw-bold py-12 text-center text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>Follow Ups</th>
                  <th scope="col" className="fw-bold py-12 text-center text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>Prospects</th>
                  <th scope="col" className="fw-bold py-12 text-center text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>Not Interested</th>
                  <th scope="col" className="fw-bold py-12 text-center text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>Blocked</th>
                  <th scope="col" className="fw-bold py-12 text-center text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>Booked</th>
                </tr>
              </thead>
              <tbody>
                {summaryData.map((item, idx) => (
                  <tr key={idx}>
                    <td className="text-sm fw-medium px-16">{item.expoName || 'Unknown Expo'}</td>
                    <td className="text-center text-sm fw-bold">{item.Assigns}</td>
                    <td className="text-center text-sm fw-bold">{item.RNR}</td>
                    <td className="text-center text-sm fw-bold">{item.Followups}</td>
                    <td className="text-center text-sm fw-bold">{item.Prospects}</td>
                    <td className="text-center text-sm fw-bold">{item.NotInterested}</td>
                    <td className="text-center text-sm fw-bold">{item.Blocked}</td>
                    <td className="text-center text-sm fw-bold">{item.Booked}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}



        <div className="table-responsive border radius-8 bg-white overflow-x-auto shadow-sm mb-16">
          <table className="table bordered-table sm-table mb-0" style={{ minWidth: "1100px" }}>
            <thead>
              <tr style={{ background: "linear-gradient(135deg, #bb0007, #ed1c24)" }}>
                <th scope="col" className="fw-bold text-white py-12 px-16" style={{ width: "60px" }}>
                  S.No
                </th>
                <th scope="col" className="fw-bold text-white py-12 text-nowrap" style={{ width: "160px" }}>
                  Date & Time
                </th>
                <th scope="col" className="fw-bold text-white py-12" style={{ width: "200px" }}>
                  Expo Name
                </th>
                <th scope="col" className="fw-bold text-white py-12" style={{ width: "260px" }}>
                  Client Details
                </th>
                <th scope="col" className="fw-bold text-white py-12 text-center" style={{ width: "150px" }}>
                  Updated Status
                </th>
                <th scope="col" className="fw-bold text-white py-12" style={{ width: "210px" }}>
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-24">
                    <div className="spinner-border text-danger" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : reportData.length > 0 ? (
                reportData.map((item, index) => (
                  <tr key={index}>
                    <td className="py-10 px-16">
                      <div>{currentPage * rowsPerPage + index + 1}</div>
                    </td>
                    <td className="py-10 text-nowrap">
                      <div className="fw-medium text-dark">{formatDateTime(item.createdAt || item.date)}</div>
                    </td>
                    <td className="py-10">
                      <div className="fw-semibold text-dark">{item.expoName || "N/A"}</div>
                    </td>
                    <td className="py-10">
                      <div>
                        {item.client ? (
                          <>
                            <div className="fw-semibold text-dark">{item.client.companyName}</div>
                            <div className="text-xs text-secondary-light mt-1">
                              <strong>Key Person:</strong> {item.client.keyPerson || "N/A"}
                            </div>
                            <div className="text-xs text-secondary-light">
                              <strong>Mobile:</strong> {item.client.mobile1 || item.client.mobile || "N/A"}
                            </div>
                            <div className="text-xs text-secondary-light">
                              <strong>Email:</strong> {item.client.email || "N/A"}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </div>
                    </td>
                    <td className="py-10 text-center">
                      <span className={`badge ${getStatusBadgeClass(item.status)} px-16 py-6 radius-4 fw-bold`}>
                        {item.status || "N/A"}
                      </span>
                      {item.date && (
                        <div className="text-xxs text-secondary-light mt-4 fw-semibold">
                          Next Date: {formatDate(item.date)}
                        </div>
                      )}
                    </td>
                    <td className="py-10">
                      <div className="text-wrap text-dark" style={{ maxWidth: "210px", wordBreak: "break-word" }}>
                        {item.remarks || "-"}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-24 text-secondary-light">
                    No client status history found for this event manager.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value));
            setCurrentPage(0);
          }}
          totalRecords={totalRecords}
        />
      </div>
    </div>
  );
};

export default EventManagerHistoryLayer;
